import React, {
  FC, cloneElement, useMemo, useCallback, RefObject, ReactElement, ReactNode, HTMLAttributes, KeyboardEvent,
} from 'react';
import { Booleanish } from 'ia-react-core/~/interfaces/Booleanish';

import useMenuKeyboardNavigation from './hooks/_useMenuKeyboardNavigation';

interface MenuTargetProps {
  items: ReactElement[];
  menuContainerId: string;
  target: ReactElement<{
    children?: ReactNode;
    disabled?: boolean;
    'data-testid'?: string;
    ref?: (current: HTMLElement) => void;
  } & HTMLAttributes<HTMLElement>>;
  isVisible?: boolean;
  disabled: boolean;
  selectedItem: ReactElement;
  selectedItemIndex: number;
  itemsRefs: RefObject<HTMLLIElement>[];
  targetRef: RefObject<HTMLElement>;
  updateRef: (element: HTMLElement) => void;
  setSelectedItem: (selectedItem: ReactElement) => void;
}

const MenuTargetDefaultProps = {};

const MenuTarget: FC<MenuTargetProps> = React.forwardRef(({
  items,
  selectedItemIndex,
  menuContainerId,
  target,
  disabled,
  updateRef,
  selectedItem,
  setSelectedItem,
  itemsRefs,
  targetRef,
  isVisible,
  ...props
}, ref) => {
  const mergeRefs = useCallback((current: HTMLElement) => {
    updateRef(current);
    (ref as any)?.(current);
  }, [ref, updateRef]);

  const handleToggle = useCallback(() => {
    targetRef?.current.click();
  }, [targetRef]);

  const handleClose = useCallback(() => {
    targetRef?.current.click();
  }, [targetRef]);

  const handleSelected = useCallback((newSelectedIndex: number) => {
    setSelectedItem(items[newSelectedIndex]);
  }, [items, setSelectedItem]);

  const handleItemAction = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (!selectedItem?.props.disabled) {
      if (selectedItem?.props?.onClick) {
        selectedItem?.props?.onClick(event);
      } else {
        const selectedElement = itemsRefs[selectedItemIndex];
        selectedElement?.current.click();
      }

      handleToggle();
    }
  }, [selectedItem?.props, handleToggle, itemsRefs, selectedItemIndex]);

  const { handleKeyboardNavigation } = useMenuKeyboardNavigation({
    items,
    selectedItemIndex,
    handleSelected,
    handleToggle,
    handleClose,
    handleItemAction,
    isOpened: isVisible,
  });

  const menuTarget = useMemo(() => {
    if (!target) return null;

    return (
      cloneElement(target, {
        'data-testid': 'Menu',
        'aria-controls': menuContainerId,
        'aria-haspopup': 'true',
        'aria-expanded': String(isVisible) as Booleanish,
        'aria-disabled': String(disabled)as Booleanish,
        ...props,
        onKeyDown: handleKeyboardNavigation,
        disabled,
        ref: mergeRefs,
      }, target?.props?.children)
    );
  }, [disabled, handleKeyboardNavigation, isVisible, menuContainerId, mergeRefs, props, target]);

  return menuTarget;
});

MenuTarget.defaultProps = MenuTargetDefaultProps;

export default MenuTarget;
